import { Box, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

type EcosystemHeadQuestionnaireSubmitConfirmModalProps = {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
  /** When true, warn that 1 collaboration credit will be deducted on submit. */
  joiningCreditsEnabled?: boolean;
  title?: string;
  message?: string;
  confirmLabel?: string;
};

export default function EcosystemHeadQuestionnaireSubmitConfirmModal({
  opened,
  onClose,
  onConfirm,
  loading = false,
  joiningCreditsEnabled = false,
  title = "Submit questionnaire?",
  message = "Are you sure you want to submit your answers? You cannot edit or resubmit them after submission.",
  confirmLabel = "Submit answers",
}: EcosystemHeadQuestionnaireSubmitConfirmModalProps) {
  const isMobile = useMediaQuery("(max-width: 47.99em)", false, { getInitialValueInEffect: true });

  return (
    <Modal
      opened={opened}
      onClose={() => !loading && onClose()}
      title={
        <Text fw={600} fz="lg" style={{ textWrap: "balance" }}>
          {title}
        </Text>
      }
      centered={!isMobile}
      size="md"
      radius="md"
      padding="lg"
      zIndex={1300}
      styles={{
        inner: isMobile
          ? {
              alignItems: "flex-start",
              paddingTop: "calc(var(--app-shell-header-height, 60px) + 12px)",
              paddingBottom: 12,
              paddingInline: 12,
            }
          : undefined,
        body: { overflowX: "hidden" },
      }}
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
    >
      <Stack gap="lg">
        <Text size="sm" c="dimmed" style={{ lineHeight: 1.55, textWrap: "pretty" }}>
          {message}
        </Text>

        {joiningCreditsEnabled ? (
          <Box
            pl="md"
            py="xs"
            style={{
              borderLeft: "3px solid var(--mantine-color-gray-4)",
              background: "var(--mantine-color-gray-0)",
              borderRadius: "0 8px 8px 0",
            }}
          >
            <Text size="sm" c="dimmed" style={{ lineHeight: 1.55, textWrap: "pretty" }}>
              A one-time joining fee is required. After you confirm, you will pay via Razorpay. The fee is
              non-refundable.
            </Text>
          </Box>
        ) : null}

        <Group gap="sm" grow wrap="nowrap" style={{ width: "100%" }}>
          <Button
            variant="default"
            onClick={onClose}
            disabled={loading}
            style={{ flex: 1, minWidth: 0 }}
            styles={{ label: { whiteSpace: "normal", textAlign: "center", lineHeight: 1.25 } }}
          >
            Go back
          </Button>
          <Button
            onClick={onConfirm}
            loading={loading}
            style={{ flex: 1, minWidth: 0 }}
            styles={{ label: { whiteSpace: "normal", textAlign: "center", lineHeight: 1.25 } }}
          >
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
